import { HoldersModel } from './HoldersModel';
import { KeyLevelsModel } from './KeyLevelsModel/KeyLevelsModel';
import { createDefaultActiveCategories } from './KeyLevelsModel/state/types';
import { VolumeProfileModel } from './VolumeProfileModel';
import { ChronoUnit, LocalDate } from '@js-joda/core';
import { computed, createModel, effect, signal } from '@preact/signals-react';
import type { ReadonlySignal } from '@preact/signals-react';
import type { QueryObserverResult } from '@tanstack/query-core';
import { Slugs } from '@thinkalpha/common/constants/slugs.js';
// `Period` must come from common's re-export, not `@js-joda/core` directly:
// the wire codec checks `instanceof Period` and Periods constructed via a
// different js-joda module instance fail equality / instanceof tests.
import { Period } from '@thinkalpha/common/schemas/codecs.js';
import type { IndicatorImportRef } from '@thinkalpha/platform-ws-client/contracts/dictionary.js';
import type { OutputColumn } from '@thinkalpha/platform-ws-client/contracts/table.js';
import { AlphaEyeWidgetContent } from '@thinkalpha/platform-ws-client/contracts/widget/alpha-eye.js';
import type {
    AlphaEyeDataListFontSize,
    AlphaEyeKeyLevel,
    AlphaEyeKeyLevelCategory,
    AlphaEyeMainSectionId,
    AlphaEyeNewsSubTabId,
    AlphaEyePageId,
    AlphaEyeSettings,
} from '@thinkalpha/platform-ws-client/contracts/widget/alpha-eye.js';
import { DisposableBox } from '@thinkalpha/ui-core/DisposableBox';
import type { Logger } from '@thinkalpha/ui-ioc/core/types/Logger';
import type { WidgetType } from '@thinkalpha/ui-types/contracts/workspace';
import type { TableStatus } from 'src/components/table-view/model';
import type { InstrumentType } from 'src/features/symbol-picker/state';
import { escapeFormulaStringLiteral } from 'src/lib/escapeFormulaStringLiteral';
import { getUnderlyingSymbolFromInstrument, parseInstrument } from 'src/lib/instrumentUtils';
import { createTable } from 'src/lib/table';
import { FilterStage, OutputStage, SourceStage } from 'src/lib/tableStages';
import type { Stage } from 'src/lib/tableStages';
import type { AnalystRatingsModel } from 'src/models/AnalystRatingsModel';
import { AnalystRatingsModelImpl } from 'src/models/AnalystRatingsModel/impl';
import type { LightweightChartModel } from 'src/models/LightweightChartModel';
import { LinkingMailboxModel } from 'src/models/LinkingMailboxModel';
import type { TableModel } from 'src/models/TableModel';
import type { WorkspaceService } from 'src/services/WorkspaceService';
import type { WidgetHostContainer } from 'src/services/WorkspaceService/Container/Container';
import { ContainerChannelSlice } from 'src/services/WorkspaceService/Container/slices/ContainerChannelSlice';
import { ContainerWidgetTabSlice } from 'src/services/WorkspaceService/Container/slices/ContainerWidgetTabSlice';
import type { LinkEvent } from 'src/services/WorkspaceService/linking';
import type { WidgetId } from 'src/services/WorkspaceService/selfOwnedWidget';
import type { ReactBindings } from 'src/types/bindings';
import { EventBusBuilder, MergedEventBus } from 'src/types/EventBus/EventBus';
import { z } from 'zod';

const ALLOWED_INSTRUMENT_TYPES: InstrumentType[] = ['equity'];
const EMPTY_KEY_LEVELS: readonly AlphaEyeKeyLevel[] = Object.freeze([]);
const ALPHA_EYE_ACCEPTED_INSTRUMENT_LINK_SOURCES = new Set<WidgetType>([
    'results',
    'watchlist',
    'order-blotter',
    'positions',
    'executions',
    'orders',
    'locates',
    'lab',
    'alpha-lens',
    'alpha-eye',
    'searchalpha',
    'scanner',
    'screener',
    'news',
    'events',
    'filings',
    'tradingview-chart',
]);

// Re-export the wire contract enum so consumers keep using the
// `AlphaEyeNewsSubTabId` import path that this module previously owned.
export { AlphaEyeNewsSubTabId } from '@thinkalpha/platform-ws-client/contracts/widget/alpha-eye.js';

/**
 * Flatten a Joda `Period` (which can mix years / months / days) into a
 * calendar-day count for `LightweightChartModel.setHistoryLookbackDays`.
 * Computed against today's date so `P1Y` correctly yields 365 or 366
 * depending on the calendar year.
 */
export function lookbackToDays(period: Period): number {
    const today = LocalDate.now();
    return ChronoUnit.DAYS.between(today, today.plus(period));
}

/**
 * Persisted widget-instance state. Aliased to the wire contract
 * `AlphaEyeWidgetContent` so the local model and the workspace PUT payload
 * stay in lockstep — this widget is server-persisted (no longer
 * `isLocalOnly`) and the workspace `Widget` discriminated union expects
 * exactly this shape under the `'alpha-eye'` discriminator. `chartLookback`
 * is a `Period` after schema decode; on the wire it travels as an ISO-8601
 * string (`P3M` / `P6M` / `P1Y`).
 */
export type AlphaEyeWidgetState = z.output<typeof AlphaEyeWidgetContent>;

/**
 * Duck-typed encoder: detect any `Period`-shaped object (regardless of which
 * js-joda module instance constructed it) and emit its ISO-8601 string. In
 * dev with `link:`-resolved platform-ws-client, the linked client carries a
 * separate `@thinkalpha/common` (and transitively a separate `@js-joda/core`)
 * from the one the UI imports — so `v instanceof Period` from this module's
 * Period class would fail for Periods returned by the linked-client schema.
 * `toJSON()` is on every Period's prototype and returns the canonical
 * `P…` string, which is exactly the codec's input shape.
 */
function isPeriodLike(v: unknown): v is { toJSON(): string } {
    return (
        typeof v === 'object' &&
        v !== null &&
        typeof (v as { toJSON?: unknown }).toJSON === 'function' &&
        typeof (v as { years?: unknown }).years === 'function' &&
        typeof (v as { months?: unknown }).months === 'function' &&
        typeof (v as { days?: unknown }).days === 'function'
    );
}

/**
 * Normalize a `Period` from any js-joda instance into this module's local
 * `Period` so equality / `LocalDate.plus(...)` calls in `lookbackToDays`
 * stay within a single module's class identity.
 */
function normalizeToLocalPeriod(p: { years(): number; months(): number; days(): number }): Period {
    return Period.of(p.years(), p.months(), p.days());
}

/**
 * Parsing schema. `widget.toJSON()` returns a live `Period` instance for
 * `chartLookback` (matching the in-memory state type), but `Container/impl.ts`
 * re-parses that output against this schema as a defensive validation step.
 * The bare `PeriodCodec` only accepts ISO strings — pre-encoding any
 * `Period`-shaped input back to its string form here lets the same schema
 * accept both fresh-from-the-wire strings *and* in-memory Period instances.
 * After decode, the transform normalizes the result to this module's
 * `Period` class so downstream `LocalDate.plus(...)` and `.equals(...)`
 * work reliably regardless of which js-joda instance produced it.
 */
const ChartLookbackTolerant = z.preprocess(
    (v) => (isPeriodLike(v) ? v.toJSON() : v),
    AlphaEyeWidgetContent.shape.chartLookback,
);

const AlphaEyeInitializedWidgetContent = AlphaEyeWidgetContent.extend({
    symbol: AlphaEyeWidgetContent.shape.symbol.refine((symbol) => symbol.trim().length > 0, {
        message: 'AlphaEye initialized state requires a symbol',
    }),
});

export const alphaEyeWidgetStateSchema = AlphaEyeInitializedWidgetContent.extend({
    chartLookback: ChartLookbackTolerant,
}).transform((s): AlphaEyeWidgetState => {
    const chartLookback = s.chartLookback ? normalizeToLocalPeriod(s.chartLookback) : undefined;
    if (s.currentPageId === 'news-summary') {
        return {
            ...s,
            currentPageId: 'news',
            activeNewsSubTabId: 'summary',
            chartLookback,
        };
    }
    return {
        ...s,
        chartLookback,
    };
}) satisfies z.ZodType<AlphaEyeWidgetState>;

export type AlphaEyeWidgetInitializingState = Omit<Partial<AlphaEyeWidgetState>, 'symbol'> & {
    initialization: 'default-symbol';
    symbol?: never;
};

export type AlphaEyeWidgetInitialState = AlphaEyeWidgetState | AlphaEyeWidgetInitializingState;

export type AlphaEyeWidgetRuntimeState =
    | { type: 'uninitialized' }
    | { type: 'initializing' }
    | { type: 'ready'; activePage: AlphaEyeActivePage }
    | { type: 'error'; error: Error };

const alphaEyeWidgetInitializingStateSchema = AlphaEyeWidgetContent.omit({ symbol: true })
    .extend({
        initialization: z.literal('default-symbol'),
        chartLookback: ChartLookbackTolerant,
    })
    .transform((s): AlphaEyeWidgetInitializingState => {
        const chartLookback = s.chartLookback ? normalizeToLocalPeriod(s.chartLookback) : undefined;
        if (s.currentPageId === 'news-summary') {
            return {
                ...s,
                currentPageId: 'news',
                activeNewsSubTabId: 'summary',
                chartLookback,
            };
        }
        return {
            ...s,
            chartLookback,
        };
    });

export const alphaEyeWidgetInitialStateSchema = z.union([
    alphaEyeWidgetStateSchema,
    alphaEyeWidgetInitializingStateSchema,
]) satisfies z.ZodType<AlphaEyeWidgetInitialState>;

const DEFAULT_CURRENT_PAGE_ID: AlphaEyePageId = 'main';
const DEFAULT_ACTIVE_MAIN_SUB_TAB_ID: AlphaEyeMainSectionId = 'general';
const DEFAULT_ACTIVE_NEWS_SUB_TAB_ID: AlphaEyeNewsSubTabId = 'news';
const DEFAULT_DATA_LIST_FONT_SIZE: AlphaEyeDataListFontSize = 'small';
const DEFAULT_IS_CHART_COLLAPSED = false;
const DEFAULT_CHART_LOOKBACK: Period = Period.ofMonths(3);

function normalizeCurrentPageId(pageId: AlphaEyePageId): AlphaEyePageId {
    return pageId === 'news-summary' ? 'news' : pageId;
}

function getInitialActiveNewsSubTabId(initialState: AlphaEyeWidgetInitialState): AlphaEyeNewsSubTabId {
    return initialState.currentPageId === 'news-summary'
        ? 'summary'
        : (initialState.activeNewsSubTabId ?? DEFAULT_ACTIVE_NEWS_SUB_TAB_ID);
}

type AlphaEyeModelInitBase = {
    persisted: AlphaEyeWidgetInitialState;
    logger: Logger;
    loadDefaultSymbol: () => Promise<string>;
    settingsQuery: ReadonlySignal<QueryObserverResult<AlphaEyeSettings>>;
    queryObserverModelFactory: ReactBindings['QueryObserverModelFactory'];
    queryClient: ReactBindings['QueryClient'];
    tableModelFactory: ReactBindings['TableModelFactory'];
    slugMapServiceProvider: ReactBindings['SlugMapServiceProvider'];
    chartModel: LightweightChartModel;
    lastPrice: ReadonlySignal<number | null>;
};

type AlphaEyeWorkspaceModelInit = AlphaEyeModelInitBase & {
    mode: 'workspace';
    container: WidgetHostContainer;
    tabId: WidgetId;
    workspaceService: Pick<WorkspaceService, 'publishLinkIntent' | 'subscribeLinkEvents'>;
};

type AlphaEyeStandaloneModelInit = AlphaEyeModelInitBase & {
    mode: 'standalone';
};

type AlphaEyeModelInit = AlphaEyeWorkspaceModelInit | AlphaEyeStandaloneModelInit;

/**
 * Value shape of the model's active-sub-tab connection-status signal. Mirrors
 * the eventBus `widget::connectionStatus` signal shape used by every other
 * widget; consumed by the reusable `ConnectionStatus.svelte`.
 */
export interface MainSubTabConnectionStatusValue {
    status: TableStatus;
    progress: number | undefined;
    failureCount: number;
}

export type AlphaEyeMainActivePage = {
    readonly pageId: 'main';
    readonly chartModel: LightweightChartModel;
};

export type AlphaEyePassiveActivePage = {
    readonly pageId: 'events' | 'news' | 'filings';
};

export type AlphaEyeAnalystRecommendationsActivePage = {
    readonly pageId: 'analyst-recommendations';
    readonly analystRatingsModel: AnalystRatingsModel;
};

export type AlphaEyeHoldersActivePage = {
    readonly pageId: 'holders';
    readonly holdersModel: HoldersModel;
};

export type AlphaEyeVolumeProfileActivePage = {
    readonly pageId: 'volume-profile';
    readonly volumeProfileModel: VolumeProfileModel;
};

export type AlphaEyeKeyLevelsActivePage = {
    readonly pageId: 'key-levels';
    readonly keyLevelsModel: KeyLevelsModel;
    readonly keyLevelsStatsTable: TableModel;
};

export type AlphaEyeActivePage =
    | AlphaEyeMainActivePage
    | AlphaEyePassiveActivePage
    | AlphaEyeAnalystRecommendationsActivePage
    | AlphaEyeHoldersActivePage
    | AlphaEyeVolumeProfileActivePage
    | AlphaEyeKeyLevelsActivePage;

export type AlphaEyeWidgetRuntimeAction =
    | { readonly type: 'defaultSymbolInitializationStarted' }
    | { readonly type: 'activePageReady'; readonly activePage: AlphaEyeActivePage }
    | { readonly type: 'activePageFailed'; readonly error: Error }
    | { readonly type: 'defaultSymbolInitializationFailed'; readonly error: Error };

export function alphaEyeWidgetRuntimeReducer(
    state: AlphaEyeWidgetRuntimeState,
    action: AlphaEyeWidgetRuntimeAction,
): AlphaEyeWidgetRuntimeState {
    switch (action.type) {
        case 'defaultSymbolInitializationStarted':
            return state.type === 'uninitialized' ? { type: 'initializing' } : state;
        case 'activePageReady':
            return { type: 'ready', activePage: action.activePage };
        case 'activePageFailed':
            return { type: 'error', error: action.error };
        case 'defaultSymbolInitializationFailed':
            return state.type === 'initializing' ? { type: 'error', error: action.error } : state;
    }
}

function isInitializedAlphaEyeWidgetState(state: AlphaEyeWidgetInitialState): state is AlphaEyeWidgetState {
    return 'symbol' in state && typeof state.symbol === 'string' && state.symbol.trim().length > 0;
}

function initializationError(error: unknown): Error {
    return error instanceof Error ? error : new Error(String(error));
}

export const AlphaEyeModel = createModel((init: AlphaEyeModelInit) => {
    const initialState = init.persisted;
    const hasInitializedState = isInitializedAlphaEyeWidgetState(initialState);
    const runtimeState = signal<AlphaEyeWidgetRuntimeState>({ type: 'uninitialized' });
    const dispatchRuntime = (
        action: AlphaEyeWidgetRuntimeAction,
    ): { previous: AlphaEyeWidgetRuntimeState; next: AlphaEyeWidgetRuntimeState } => {
        const previous = runtimeState.value;
        const next = alphaEyeWidgetRuntimeReducer(previous, action);
        runtimeState.value = next;
        return { previous, next };
    };
    const symbol = signal(hasInitializedState ? initialState.symbol : '');
    const isChartCollapsed = signal(initialState.isChartCollapsed ?? DEFAULT_IS_CHART_COLLAPSED);
    const currentPageId = signal<AlphaEyePageId>(
        normalizeCurrentPageId(initialState.currentPageId ?? DEFAULT_CURRENT_PAGE_ID),
    );
    const activeMainSubTabId = signal<AlphaEyeMainSectionId>(
        initialState.activeMainSubTabId ?? DEFAULT_ACTIVE_MAIN_SUB_TAB_ID,
    );
    const activeNewsSubTabId = signal<AlphaEyeNewsSubTabId>(getInitialActiveNewsSubTabId(initialState));
    const dataListFontSize = signal<AlphaEyeDataListFontSize>(
        initialState.dataListFontSize ?? DEFAULT_DATA_LIST_FONT_SIZE,
    );
    const goToDate = signal<string | null>(initialState.goToDate ?? null);
    const displayMonth = signal<string | null>(initialState.displayMonth ?? null);
    const chartLookback = signal<Period>(initialState.chartLookback ?? DEFAULT_CHART_LOOKBACK);
    const persistedActiveCategories = initialState.keyLevels?.activeCategories;
    const keyLevelsActiveCategories = signal<ReadonlySet<AlphaEyeKeyLevelCategory> | null>(
        persistedActiveCategories === undefined
            ? createDefaultActiveCategories()
            : persistedActiveCategories === null
              ? null
              : new Set(persistedActiveCategories),
    );
    const activePageBox = new DisposableBox<Disposable>();

    // Registry of per-sub-tab TableModels — pushed in by MainSubTab.svelte
    // when it mounts each sub-tab table. Driven through a Map signal so the
    // connection-status computed below can react to registration changes.
    const mainSubTabTables = signal(new Map<AlphaEyeMainSectionId, TableModel>());

    const dirty = signal(false);
    const allowedInstrumentTypes = signal<InstrumentType[]>(ALLOWED_INSTRUMENT_TYPES);

    const symbolReadonly: ReadonlySignal<string> = computed(() => symbol.value);
    const runtimeStateReadonly: ReadonlySignal<AlphaEyeWidgetRuntimeState> = computed(() => runtimeState.value);
    const isChartCollapsedReadonly: ReadonlySignal<boolean> = computed(() => isChartCollapsed.value);
    const currentPageIdReadonly: ReadonlySignal<AlphaEyePageId> = computed(() => currentPageId.value);
    const activeMainSubTabIdReadonly: ReadonlySignal<AlphaEyeMainSectionId> = computed(() => activeMainSubTabId.value);
    const activeNewsSubTabIdReadonly: ReadonlySignal<AlphaEyeNewsSubTabId> = computed(() => activeNewsSubTabId.value);
    const dataListFontSizeReadonly: ReadonlySignal<AlphaEyeDataListFontSize> = computed(() => dataListFontSize.value);
    const goToDateReadonly: ReadonlySignal<string | null> = computed(() => goToDate.value);
    const displayMonthReadonly: ReadonlySignal<string | null> = computed(() => displayMonth.value);
    const chartLookbackReadonly: ReadonlySignal<Period> = computed(() => chartLookback.value);
    const keyLevelsActiveCategoriesReadonly: ReadonlySignal<ReadonlySet<AlphaEyeKeyLevelCategory> | null> = computed(
        () => keyLevelsActiveCategories.value,
    );
    const dirtyReadonly: ReadonlySignal<boolean> = computed(() => dirty.value);
    const allowedInstrumentTypesReadonly: ReadonlySignal<InstrumentType[]> = computed(
        () => allowedInstrumentTypes.value,
    );
    const setKeyLevelsActiveCategories = (next: ReadonlySet<AlphaEyeKeyLevelCategory> | null): void => {
        if (keyLevelsActiveCategories.value === next) return;
        keyLevelsActiveCategories.value = next;
        dirty.value = true;
    };
    const createKeyLevelsStatsTable = (activeSignal: ReadonlySignal<boolean>): TableModel => {
        const slugMapServiceProvider = init.slugMapServiceProvider;

        const queryOptions = computed(() => {
            const active = activeSignal.value;
            const currentSymbol = symbolReadonly.value;
            return {
                queryKey: ['alpha-eye-key-levels-stats', currentSymbol],
                gcTime: 0,
                staleTime: 0,
                queryFn: async () => {
                    if (!active || !currentSymbol) return null;
                    const slugMapService = await slugMapServiceProvider();
                    const slugMap = slugMapService.slugMap.value;
                    const symbolIndicator = slugMap[Slugs.symbol];
                    const lastIndicator = slugMap[Slugs.last];
                    const changeIndicator = slugMap[Slugs.change];
                    const volumeIndicator = slugMap[Slugs.volume];
                    const barHighIndicator = slugMap[Slugs.barHigh];
                    const barLowIndicator = slugMap[Slugs.barLow];

                    const symbolRef: IndicatorImportRef = {
                        id: symbolIndicator.id,
                        alias: symbolIndicator.symbol,
                    };
                    const columns: OutputColumn[] = [
                        { type: 'indicator', id: 'kl::last', indicator: { id: lastIndicator.id } },
                        { type: 'indicator', id: 'kl::change', indicator: { id: changeIndicator.id } },
                        {
                            type: 'formula',
                            id: 'kl::percent-change',
                            formula: 'change / (last - change)',
                            imports: [
                                { id: lastIndicator.id, alias: 'last' },
                                { id: changeIndicator.id, alias: 'change' },
                            ],
                        },
                        { type: 'indicator', id: 'kl::volume', indicator: { id: volumeIndicator.id } },
                        { type: 'indicator', id: 'kl::high', indicator: { id: barHighIndicator.id } },
                        { type: 'indicator', id: 'kl::low', indicator: { id: barLowIndicator.id } },
                        {
                            type: 'formula',
                            id: 'kl::prev-close',
                            formula: 'last - change',
                            imports: [
                                { id: lastIndicator.id, alias: 'last' },
                                { id: changeIndicator.id, alias: 'change' },
                            ],
                        },
                    ];

                    let stage: Stage = SourceStage.allForKey('ticker');
                    stage = FilterStage.fromInputStage(stage, {
                        type: 'filter',
                        formula: `${symbolIndicator.symbol}=='${escapeFormulaStringLiteral(currentSymbol)}'`,
                        imports: [symbolRef],
                    });
                    stage = OutputStage.fromInputStage(stage, {}, columns);
                    return createTable({ leafStage: stage.build() });
                },
            };
        });

        const table = init.tableModelFactory();
        table.init({ queryOptions, initBounds: { firstRow: 0, windowSize: 1 }, initThrottle: 1000 });
        return table;
    };

    const createActivePage = (pageId: AlphaEyePageId): { activePage: AlphaEyeActivePage; disposable?: Disposable } => {
        switch (pageId) {
            case 'main': {
                const stack = new DisposableStack();
                stack.defer(
                    effect(() => {
                        const collapsed = isChartCollapsedReadonly.value;
                        const currentSymbol = symbolReadonly.value;
                        const lookback = chartLookbackReadonly.value;
                        if (collapsed || !currentSymbol) return;
                        init.chartModel.setSymbol(currentSymbol);
                        init.chartModel.setHistoryLookbackDays(lookbackToDays(lookback));
                    }),
                );
                stack.defer(() => {
                    init.chartModel.setSymbol('');
                });
                return {
                    activePage: { pageId, chartModel: init.chartModel },
                    disposable: stack,
                };
            }
            case 'news-summary':
                return createActivePage('news');
            case 'events':
            case 'news':
            case 'filings':
                return { activePage: { pageId } };
            case 'analyst-recommendations': {
                const enabled = signal(true);
                const analystRatingsModel = new AnalystRatingsModelImpl(init.queryObserverModelFactory, init.logger, {
                    symbol: symbolReadonly,
                    enabled,
                });
                return {
                    activePage: { pageId, analystRatingsModel },
                    disposable: analystRatingsModel,
                };
            }
            case 'holders': {
                const holdersModel = new HoldersModel(init.queryObserverModelFactory, init.queryClient, {
                    symbol: symbolReadonly,
                    enabled: signal(true),
                    lastPrice: init.lastPrice,
                });
                return {
                    activePage: { pageId, holdersModel },
                    disposable: holdersModel,
                };
            }
            case 'volume-profile': {
                const volumeProfileModel = new VolumeProfileModel();
                return {
                    activePage: { pageId, volumeProfileModel },
                    disposable: volumeProfileModel,
                };
            }
            case 'key-levels': {
                const stack = new DisposableStack();
                const enabled = signal(true);
                const keyLevelsQueryTable = stack.use(init.tableModelFactory());
                const keyLevelsConfig = computed(() => init.settingsQuery.value.data?.keyLevels ?? EMPTY_KEY_LEVELS);
                const keyLevelsModel = stack.use(
                    new KeyLevelsModel({
                        slugMapServiceProvider: init.slugMapServiceProvider,
                        tableModel: keyLevelsQueryTable,
                        initialActiveCategories: keyLevelsActiveCategoriesReadonly.value,
                        symbol: symbolReadonly,
                        enabled,
                        keyLevels: keyLevelsConfig,
                    }),
                );

                {
                    const activeCategoriesSignal = keyLevelsModel.activeCategories;
                    let lastActiveCategories = activeCategoriesSignal.value;
                    const unsubscribe = activeCategoriesSignal.subscribe((next) => {
                        if (next === lastActiveCategories) return;
                        lastActiveCategories = next;
                        setKeyLevelsActiveCategories(next);
                    });
                    stack.use({ [Symbol.dispose]: () => unsubscribe() });
                }

                const keyLevelsStatsTable = stack.use(createKeyLevelsStatsTable(enabled));
                {
                    let lastSymbol = symbolReadonly.value;
                    const unsubscribe = symbolReadonly.subscribe((next) => {
                        if (next === lastSymbol) return;
                        lastSymbol = next;
                        keyLevelsStatsTable.destroy();
                    });
                    stack.use({ [Symbol.dispose]: () => unsubscribe() });
                }

                return {
                    activePage: { pageId, keyLevelsModel, keyLevelsStatsTable },
                    disposable: stack,
                };
            }
        }
    };

    const activateCurrentPage = (): void => {
        try {
            const { activePage, disposable } = createActivePage(currentPageId.value);
            if (disposable) activePageBox.next(disposable);
            else activePageBox.clear();
            dispatchRuntime({ type: 'activePageReady', activePage });
        } catch (error: unknown) {
            const normalizedError = initializationError(error);
            init.logger.error('Failed to initialize AlphaEye active page', {
                error: normalizedError,
                pageId: currentPageId.value,
                ...(init.mode === 'workspace' ? { tabId: init.tabId } : { mode: init.mode }),
            });
            dispatchRuntime({ type: 'activePageFailed', error: normalizedError });
        }
    };

    let disposed = false;
    effect(() => () => {
        disposed = true;
        activePageBox[Symbol.dispose]();
        init.chartModel[Symbol.dispose]();
    });

    // Stable preact-computed signal for the active sub-tab's full connection
    // state. Identity is fixed for the lifetime of the model; Svelte's
    // auto-store `$prefix` consumes the value reactively without an
    // `$effect` bridge. Shape matches the standard `widget::connectionStatus`
    // eventBus signal used by every other widget so a single `ConnectionStatus`
    // component can render it everywhere.
    const mainSubTabConnectionStatus: ReadonlySignal<MainSubTabConnectionStatusValue> = computed(() => {
        const id = activeMainSubTabId.value;
        const t = mainSubTabTables.value.get(id);
        return {
            status: t?.connectionStatus.value ?? 'uninitialized',
            progress: t?.progress.value,
            failureCount: t?.failureCount.value ?? 0,
        };
    });

    const mainSubTabTableKey: ReadonlySignal<string> = computed(() => {
        const id = activeMainSubTabId.value;
        const t = mainSubTabTables.value.get(id);
        return t?.keySignal.value ?? '';
    });

    const setSymbolValue = (next: string, { markDirty = true }: { markDirty?: boolean } = {}): boolean => {
        if (symbol.value === next) {
            if (runtimeState.value.type !== 'ready') activateCurrentPage();
            return false;
        }
        symbol.value = next;
        if (runtimeState.value.type !== 'ready') activateCurrentPage();
        if (markDirty) dirty.value = true;
        return true;
    };

    const normalizeSymbol = (instrument: string): string | null => {
        try {
            return getUnderlyingSymbolFromInstrument(parseInstrument(instrument));
        } catch (error) {
            init.logger.error('Failed to normalize AlphaEye instrument', {
                error,
                instrument,
                ...(init.mode === 'workspace' ? { tabId: init.tabId } : { mode: init.mode }),
            });
            return null;
        }
    };

    const setSymbolFromLink = (instrument: string): void => {
        const linkedSymbol = normalizeSymbol(instrument);
        if (!linkedSymbol) {
            return;
        }
        setSymbolValue(linkedSymbol);
    };

    const publishSymbolSelection = (instrument: string): void => {
        if (init.mode !== 'workspace') return;
        void init.workspaceService
            .publishLinkIntent({
                type: 'instrument-selected',
                fromTabId: init.tabId,
                details: { instrument },
            })
            .catch((error: unknown) => {
                init.logger.error('Failed to publish AlphaEye link intent', {
                    error,
                    instrument,
                    tabId: init.tabId,
                });
            });
    };

    const setSymbolFromLocalChange = (next: string): void => {
        const normalizedSymbol = normalizeSymbol(next);
        if (!normalizedSymbol || !setSymbolValue(normalizedSymbol)) return;
        publishSymbolSelection(normalizedSymbol);
    };

    const initializeDefaultSymbol = (): void => {
        const { previous, next } = dispatchRuntime({ type: 'defaultSymbolInitializationStarted' });
        if (previous === next || next.type !== 'initializing') return;
        void init
            .loadDefaultSymbol()
            .then((next) => {
                if (runtimeState.value.type !== 'initializing') return;
                const normalizedSymbol = normalizeSymbol(next);
                if (!normalizedSymbol) {
                    throw new Error('AlphaEye default symbol is invalid');
                }
                setSymbolValue(normalizedSymbol);
            })
            .catch((error: unknown) => {
                const normalizedError = initializationError(error);
                const { previous: previousState, next: nextState } = dispatchRuntime({
                    type: 'defaultSymbolInitializationFailed',
                    error: normalizedError,
                });
                if (previousState === nextState) return;
                init.logger.error('Failed to initialize AlphaEye default symbol', {
                    error: normalizedError,
                    ...(init.mode === 'workspace' ? { tabId: init.tabId } : { mode: init.mode }),
                });
            });
    };

    const bootstrapRuntimeState = hasInitializedState ? activateCurrentPage : initializeDefaultSymbol;
    queueMicrotask(() => {
        if (disposed || runtimeState.value.type !== 'uninitialized') return;
        bootstrapRuntimeState();
    });

    const ownEventBus = EventBusBuilder.create()
        .addSignal('widget::instrument', () => symbolReadonly)
        .addSignal('widget::allowedInstrumentTypes', () => allowedInstrumentTypesReadonly)
        .addEvent('widget::instrumentChange', ({ instrument }) => {
            setSymbolFromLocalChange(instrument);
        })
        .build();

    const eventBus =
        init.mode === 'workspace'
            ? (() => {
                  const channelSlice = new ContainerChannelSlice({ container: init.container, tabId: init.tabId });
                  const widgetTabSlice = new ContainerWidgetTabSlice({
                      container: init.container,
                      tabId: init.tabId,
                      logger: init.logger,
                  });

                  const acceptsLinkEvent = (event: LinkEvent): boolean => {
                      const channel = channelSlice.channel.value;
                      return (
                          channel !== null &&
                          event.channelId === channel &&
                          event.type === 'instrument-selected' &&
                          event.from.kind === 'widget' &&
                          event.from.tabId !== init.tabId &&
                          ALPHA_EYE_ACCEPTED_INSTRUMENT_LINK_SOURCES.has(event.from.widgetType)
                      );
                  };
                  const linkingMailbox = new LinkingMailboxModel({
                      workspaceService: init.workspaceService,
                      acceptsLinkEvent,
                  });

                  effect(() => () => {
                      linkingMailbox.dispose();
                  });

                  effect(() => {
                      const linkedInstrument = linkingMailbox.instrument.value;
                      if (!linkedInstrument) return;
                      setSymbolFromLink(linkedInstrument.value);
                  });

                  return MergedEventBus.from(ownEventBus).merge(channelSlice.eventBus).merge(widgetTabSlice.eventBus);
              })()
            : ownEventBus;

    return {
        state: runtimeStateReadonly,
        symbol: symbolReadonly,
        isChartCollapsed: isChartCollapsedReadonly,
        currentPageId: currentPageIdReadonly,
        activeMainSubTabId: activeMainSubTabIdReadonly,
        activeNewsSubTabId: activeNewsSubTabIdReadonly,
        dataListFontSize: dataListFontSizeReadonly,
        goToDate: goToDateReadonly,
        displayMonth: displayMonthReadonly,
        chartLookback: chartLookbackReadonly,
        keyLevelsActiveCategories: keyLevelsActiveCategoriesReadonly,
        dirty: dirtyReadonly,
        mainSubTabConnectionStatus,
        mainSubTabTableKey,
        eventBus,
        setSymbol(next: string): void {
            setSymbolFromLocalChange(next);
        },
        setIsChartCollapsed(next: boolean): void {
            if (isChartCollapsed.value === next) return;
            isChartCollapsed.value = next;
            dirty.value = true;
        },
        setCurrentPageId(next: AlphaEyePageId): void {
            const normalizedNext = normalizeCurrentPageId(next);
            let changed = false;
            if (next === 'news-summary' && activeNewsSubTabId.value !== 'summary') {
                activeNewsSubTabId.value = 'summary';
                changed = true;
            }
            if (currentPageId.value !== normalizedNext) {
                currentPageId.value = normalizedNext;
                if (runtimeState.value.type === 'ready' || runtimeState.value.type === 'error') {
                    activateCurrentPage();
                }
                changed = true;
            }
            if (changed) dirty.value = true;
        },
        setActiveMainSubTabId(next: AlphaEyeMainSectionId): void {
            if (activeMainSubTabId.value === next) return;
            activeMainSubTabId.value = next;
            dirty.value = true;
        },
        setActiveNewsSubTabId(next: AlphaEyeNewsSubTabId): void {
            if (activeNewsSubTabId.value === next) return;
            activeNewsSubTabId.value = next;
            dirty.value = true;
        },
        setDataListFontSize(next: AlphaEyeDataListFontSize): void {
            if (dataListFontSize.value === next) return;
            dataListFontSize.value = next;
            dirty.value = true;
        },
        setGoToDate(next: string | null): void {
            if (goToDate.value === next) return;
            goToDate.value = next;
            dirty.value = true;
        },
        setDisplayMonth(next: string | null): void {
            if (displayMonth.value === next) return;
            displayMonth.value = next;
            dirty.value = true;
        },
        setChartLookback(next: Period): void {
            // Period equality is value-based — two distinct instances of
            // `Period.ofMonths(3)` are not `===`, so we must compare via
            // `.equals()` to preserve the "no-op no dirty flip" guarantee.
            if (chartLookback.value.equals(next)) return;
            chartLookback.value = next;
            dirty.value = true;
        },
        setKeyLevelsActiveCategories,
        registerMainSubTabTable(id: AlphaEyeMainSectionId, table: TableModel | null): void {
            const next = new Map(mainSubTabTables.value);
            if (table) next.set(id, table);
            else next.delete(id);
            mainSubTabTables.value = next;
        },
        refetchActiveMainSubTabTable(): void {
            const id = activeMainSubTabId.value;
            const t = mainSubTabTables.value.get(id);
            t?.refetch();
        },
        toJSON(): AlphaEyeWidgetState {
            if (runtimeState.value.type !== 'ready') {
                throw new Error(`AlphaEye widget cannot serialize while ${runtimeState.value.type}`);
            }
            return {
                symbol: symbol.value,
                isChartCollapsed: isChartCollapsed.value,
                currentPageId: currentPageId.value,
                activeMainSubTabId: activeMainSubTabId.value,
                activeNewsSubTabId: activeNewsSubTabId.value,
                dataListFontSize: dataListFontSize.value,
                goToDate: goToDate.value,
                displayMonth: displayMonth.value,
                chartLookback: chartLookback.value,
            };
        },
        markClean(): void {
            dirty.value = false;
        },
        // Lets the parent widget model flip dirty=true in response to
        // state changes it doesn't own directly — e.g. KeyLevels
        // activeCategories, which lives on KeyLevelsModel but is part of
        // the persisted AlphaEye state.
        markDirty(): void {
            if (dirty.value) return;
            dirty.value = true;
        },
    };
});

export type AlphaEyeModel = InstanceType<typeof AlphaEyeModel>;
